import * as _ from 'lodash';

import { BaseEntityUtils } from './BaseEntityUtils';
import {
  BaseTripPreferences,
  TripPreferences,
} from '../data/models/trip-preferences.model';
import { FrequencyDayOfWeek } from '../data/models/frequency-day-of-week.model';
import { HoursOfOperation } from '../data/models/trip-preferences.model';
import { TripMode } from '../data/models/trip-mode.model';
import { DateTime } from 'luxon';

class PreferenceUtils extends BaseEntityUtils {
  public getDefaults(
    pref: BaseTripPreferences | null | undefined,
    frequencyDayOfWeeks: FrequencyDayOfWeek[],
    tripModes: TripMode[],
  ): any {
    const coverageAreaIDs = (pref?.coverageAreas || []).map(
      (ca) => ca.coverageAreaID,
    );

    const timeZoneID =
      pref?.hoursOfOperations && pref?.hoursOfOperations?.length > 0
        ? pref?.hoursOfOperations[0].timeZoneID
        : 1;

    const hoursOfOperations: HoursOfOperation[] =
      this.generateDefaultHoursOfOperations(frequencyDayOfWeeks);

    if (pref?.hoursOfOperations && pref?.hoursOfOperations.length > 0) {
      pref.hoursOfOperations.forEach((value) => {
        const index = hoursOfOperations.findIndex(
          (d) => d.frequencyDayOfWeekID === value.frequencyDayOfWeekID,
        );

        hoursOfOperations[index] = {
          ...value,
          startServiceOn: DateTime.fromFormat(
            value.startServiceOn!,
            'h:m:s',
          ).toFormat('HH:mm'),
          endServiceOn: DateTime.fromFormat(
            value.endServiceOn!,
            'h:m:s',
          ).toFormat('HH:mm'),
          isActive: true,
        };
      });
    }

    if (hoursOfOperations && hoursOfOperations.length > 1) {
      hoursOfOperations?.push(hoursOfOperations.shift()!);
    }

    const sameHoursAllDays =
      pref?.hoursOfOperations.length === 7 &&
      hoursOfOperations.every(
        (hour) => hour.hoursOfOperationState === 'active',
      ) &&
      hoursOfOperations.every(
        (hour) => hour.startServiceOn === hoursOfOperations[0].startServiceOn,
      ) &&
      hoursOfOperations.every(
        (hour) => hour.endServiceOn === hoursOfOperations[0].endServiceOn,
      );

    const vehicleTripModeCapacities = tripModes
      .filter((mode) => mode.isActive)
      .map((tripMode) => ({
        tripModeID: tripMode.tripModeID,
        capacity: 0,
        tripMode: tripMode,
      }));

    if (
      pref?.vehicleTripModeCapacities &&
      pref?.vehicleTripModeCapacities?.length > 0
    ) {
      pref.vehicleTripModeCapacities.forEach((v) => {
        const index = vehicleTripModeCapacities.findIndex(
          (tripMode) => v.tripModeID === tripMode.tripModeID,
        );

        vehicleTripModeCapacities[index] = {
          ...vehicleTripModeCapacities[index],
          capacity: v.capacity,
        };
      });
    }

    return {
      coverageAreas: coverageAreaIDs || [],
      hoursOfOperations: hoursOfOperations || [],
      timeZoneID: timeZoneID,
      sameHoursAllDays: sameHoursAllDays,
      allDaysStartServiceOn: '08:00',
      allDaysEndServiceOn: '17:00',
      vehicleTripModeCapacities: vehicleTripModeCapacities || [],
      capacityOfTripsToServiceInDay:
        Number(pref?.capacityOfTripsToServiceInDay) || 1,
    };
  }

  public getPreferencesFromFormData(
    pref: BaseTripPreferences | null,
    frequencyDayOfWeeks: FrequencyDayOfWeek[],
    formData: any,
  ): BaseTripPreferences {
    let hoursOfOperations: HoursOfOperation[];

    const coverageAreaIDs: number[] = formData.coverageAreas || [];
    const coverageAreas = coverageAreaIDs.map((caid) => {
      return {
        coverageAreaID: caid,
        effectiveStartDate: '2024-06-01',
        effectiveEndDate: null,
      };
    });

    const allDaysSame = this.getDefaultBoolean(formData.sameHoursAllDays);

    if (allDaysSame) {
      hoursOfOperations = frequencyDayOfWeeks.map((day) => ({
        frequencyDayOfWeekID: day.frequencyDayOfWeekID,
        startServiceOn: DateTime.fromFormat(
          formData.allDaysStartServiceOn,
          'HH:mm',
        ).toFormat('HH:mm:ss'),
        endServiceOn: DateTime.fromFormat(
          formData.allDaysEndServiceOn,
          'HH:mm',
        ).toFormat('HH:mm:ss'),
        timeZoneID: formData.timeZoneID,
        hoursOfOperationState: 'active',
      }));
    } else {
      hoursOfOperations = frequencyDayOfWeeks.map((day) => {
        const updatedDay = formData.hoursOfOperations.find(
          (v: any) => v.frequencyDayOfWeekID === day.frequencyDayOfWeekID,
        );

        const start = updatedDay.isActive
          ? DateTime.fromFormat(updatedDay?.startServiceOn, 'HH:mm')
          : null;
        const end = updatedDay.isActive
          ? DateTime.fromFormat(updatedDay?.endServiceOn, 'HH:mm')
          : null;
        return {
          frequencyDayOfWeekID: updatedDay?.frequencyDayOfWeekID || 0,
          startServiceOn: start?.isValid ? start.toFormat('HH:mm:ss') : '',
          endServiceOn: end?.isValid ? end.toFormat('HH:mm:ss') : '',
          timeZoneID: formData.timeZoneID,
          hoursOfOperationState: 'active',
        };
      });
      hoursOfOperations = hoursOfOperations.filter(
        (h) => h.startServiceOn && h.endServiceOn,
      );
    }

    const vehicleTripModeCapacities = formData.vehicleTripModeCapacities.map(
      (tripMode: any) => ({
        tripModeID: tripMode.tripModeID,
        capacity: Number(tripMode.capacity),
      }),
    );

    const prefs: TripPreferences = {
      coverageAreas,
      hoursOfOperations: hoursOfOperations,
      vehicleTripModeCapacities: vehicleTripModeCapacities,
      capacityOfTripsToServiceInDay: Number(
        formData.capacityOfTripsToServiceInDay,
      ),
    };

    return prefs;
  }

  public getTimeZoneName(
    hoursOfOperation: HoursOfOperation[],
  ): string | undefined {
    const timeZoneName =
      hoursOfOperation.length > 0
        ? hoursOfOperation[0].timeZone?.timeZoneName
        : undefined;

    return timeZoneName;
  }

  public generateTimeOptions(): { time: string; label: string }[] {
    const hrs = [];
    for (let hour = 0; hour < 24; hour++) {
      let time = DateTime.fromObject({ hour, minute: 0 });
      hrs.push({
        time: time.toFormat('HH:mm'),
        label: time.toFormat('h:mm a'),
      });
      time = DateTime.fromObject({ hour, minute: 30 });
      hrs.push({
        time: time.toFormat('HH:mm'),
        label: time.toFormat('h:mm a'),
      });
    }

    return hrs;
  }

  public generateDefaultHoursOfOperations(
    frequencyDayOfWeeks: FrequencyDayOfWeek[],
  ): HoursOfOperation[] {
    const dayHours: HoursOfOperation[] = frequencyDayOfWeeks.map((val) => ({
      frequencyDayOfWeekID: val.frequencyDayOfWeekID,
      startServiceOn: '08:00',
      endServiceOn: '17:00',
      hoursOfOperationState: 'active',
      frequencyDayOfWeek: val,
    }));

    return dayHours.sort(
      (a, b) => a.frequencyDayOfWeekID - b.frequencyDayOfWeekID,
    );
  }
}

const preferenceUtils = new PreferenceUtils();

export default preferenceUtils;
